import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageMeta from "@/components/PageMeta";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { BookOpenText, ArrowLeft, SpeakerHigh } from "@phosphor-icons/react";
import { speak } from "@/lib/voice";
import { renderMarkdownLinks } from "@/lib/markdownLinks";

const SITE = process.env.REACT_APP_BACKEND_URL || "";

/** Injects schema.org DefinedTerm JSON-LD so Google can surface us for
 * "what does X mean" queries. Cleans up on unmount / slug change. */
function injectDefinedTermSchema(term, slug) {
  if (!term) return undefined;
  const id = "rf-defined-term-schema";
  const stale = document.getElementById(id);
  if (stale) stale.remove();
  const s = document.createElement("script");
  s.id = id;
  s.type = "application/ld+json";
  const url = `${SITE.replace(/\/$/, "")}/kitchen-help/${slug}`;
  const setUrl = `${SITE.replace(/\/$/, "")}/kitchen-help`;
  s.text = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    "@id": url,
    name: term.term,
    alternateName: term.also_known_as || [],
    description: term.definition,
    termCode: slug,
    inDefinedTermSet: {
      "@type": "DefinedTermSet",
      "@id": setUrl,
      name: "Recipe Filer Kitchen Help",
      url: setUrl,
    },
    url,
  });
  document.head.appendChild(s);
  return () => { const el = document.getElementById(id); if (el) el.remove(); };
}

export default function KitchenHelpTerm() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [term, setTerm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [relatedTerms, setRelatedTerms] = useState([]);
  const [recipes, setRecipes] = useState([]);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setRecipes([]);
    api
      .get(`/public/kitchen-help/${slug}`)
      .then((r) => {
        setTerm(r.data);
        return r.data;
      })
      .then(async (t) => {
        // Fetch the related terms so we can turn them into real links
        if (t && t.related_terms && t.related_terms.length > 0) {
          const all = await api.get("/public/kitchen-help").then((r) => r.data.items || []);
          const byLower = new Map(all.map((x) => [(x.term || "").toLowerCase(), x]));
          const list = t.related_terms
            .map((rt) => byLower.get(String(rt).toLowerCase()))
            .filter(Boolean);
          setRelatedTerms(list);
        } else {
          setRelatedTerms([]);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // Fetch recipes in parallel — a network error here shouldn't tank the page
    api
      .get(`/public/kitchen-help/${slug}/recipes`)
      .then((r) => setRecipes(r.data?.recipes || []))
      .catch(() => setRecipes([]));
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  useEffect(() => {
    if (!term) return undefined;
    return injectDefinedTermSchema(term, slug);
  }, [term, slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-rf-ink-soft">Loading…</p>
      </div>
    );
  }

  if (notFound || !term) {
    return (
      <div className="min-h-[60vh] max-w-2xl mx-auto px-6 py-24 text-center">
        {/* NB: intentionally NO PageMeta noindex here. Bots hit the SSR
            endpoint /api/render/kitchen-help/{slug} which returns a proper
            404 when the term is genuinely missing. Emitting noindex from
            the client-side SPA is dangerous — Google's rendering pipeline
            can hit a transient API 404 (rate limit, cold start, race) and
            cache "noindex" against a URL that IS actually valid. That was
            the root cause of the "76 Excluded by noindex" GSC report on
            2026-09-11. Google will de-index truly missing pages via the
            HTTP 404 chain from the SSR path; we don't need to help. */}
        <PageMeta title="Term not found — Kitchen Help" />
        <p className="overline mb-3">Kitchen Help</p>
        <h1 className="font-display text-3xl font-black text-rf-ink mb-3">Term not found</h1>
        <p className="text-rf-ink-soft mb-6">We couldn&apos;t find that entry in the glossary.</p>
        <Link to="/kitchen-help" className="btn-amber inline-flex items-center gap-2" data-testid="kh-term-back-to-index">
          <ArrowLeft size={18} weight="bold" /> Back to Kitchen Help
        </Link>
      </div>
    );
  }

  const title = (term.seo_title && term.seo_title.trim())
    || `${term.term} — what it means in cooking | Kitchen Help`;
  const description = (term.seo_description && term.seo_description.trim())
    || `${term.definition} Learn more UK-English cooking terms in the Recipe Filer Kitchen Help glossary.`;
  const canonical = `/kitchen-help/${slug}`;

  return (
    <div className="min-h-screen bg-rf-paper" data-testid="kitchen-help-term-page">
      <PageMeta
        title={title}
        description={description}
        keywords={term.seo_keywords || undefined}
        canonical={canonical}
      />
      <BreadcrumbJsonLd
        items={[
          { name: "Home", url: `${SITE}/` },
          { name: "Kitchen Help", url: `${SITE}/kitchen-help` },
          { name: term.term },
        ]}
      />

      {/* Header */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pt-14 pb-6">
        {/* Breadcrumbs */}
        <nav className="text-xs text-rf-ink-soft mb-6" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-rf-orange">Home</Link>
          <span className="mx-1.5">›</span>
          <Link to="/kitchen-help" className="hover:text-rf-orange">Kitchen Help</Link>
          <span className="mx-1.5">›</span>
          <span className="text-rf-ink font-bold">{term.term}</span>
        </nav>

        <div className="flex items-center gap-2 mb-3">
          <BookOpenText size={22} weight="duotone" className="text-rf-orange" />
          <p className="overline text-rf-orange">Kitchen Help</p>
        </div>

        <div className="flex flex-wrap items-baseline gap-4 mb-4">
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-rf-ink leading-tight"
            data-testid="kh-term-h1"
          >
            {term.term}
          </h1>
          <span
            className="inline-flex items-center px-3 py-1 rounded-full bg-rf-amber/20 text-rf-orange text-xs font-bold uppercase tracking-wider"
            data-testid="kh-term-category"
          >
            {term.category}
          </span>
        </div>

        {/* Aliases */}
        {term.also_known_as && term.also_known_as.length > 0 && (
          <p className="text-rf-ink-soft mb-6" data-testid="kh-term-aka">
            <span className="font-bold uppercase tracking-wider text-xs text-rf-ink/50">Also known as: </span>
            {term.also_known_as.join(" · ")}
          </p>
        )}

        {/* Definition + speak button */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 border border-rf-ink/10 relative">
          <button
            onClick={() => { try { speak(`${term.term}. ${term.definition}`); } catch { /* voice may be blocked */ } }}
            data-testid="kh-term-speak"
            title="Read this definition aloud"
            className="absolute top-4 right-4 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-rf-cream-alt text-rf-ink-soft hover:bg-rf-amber hover:text-rf-ink transition"
          >
            <SpeakerHigh size={14} weight="fill" /> Read aloud
          </button>
          <div className="text-lg sm:text-xl text-rf-ink leading-relaxed pr-24 [&>p]:mb-4 [&>p:last-child]:mb-0" data-testid="kh-term-definition">
            {renderMarkdownLinks(term.definition, {
              paragraphClass: "text-lg sm:text-xl text-rf-ink leading-relaxed",
              linkClass: "text-rf-orange font-bold underline hover:text-rf-ink",
            })}
          </div>
        </div>

        {/* Related terms */}
        {relatedTerms.length > 0 && (
          <div className="mt-10" data-testid="kh-term-related">
            <p className="overline mb-4">Related terms</p>
            <div className="flex flex-wrap gap-3">
              {relatedTerms.map((rt) => (
                <Link
                  key={rt.term_id}
                  to={`/kitchen-help/${rt.slug}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-rf-ink/10 text-rf-ink hover:border-rf-orange hover:text-rf-orange transition"
                  data-testid={`kh-term-related-${rt.slug}`}
                >
                  <span className="text-xs font-bold uppercase tracking-wider text-rf-orange/70">{rt.category}</span>
                  <span className="font-bold">{rt.term}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Recipes using this term — internal-linking gold for SEO */}
        {recipes.length > 0 && (
          <div className="mt-14" data-testid="kh-term-recipes">
            <p className="overline mb-1">Recipes using <span className="text-rf-orange">{term.term.toLowerCase()}</span></p>
            <h2 className="font-display text-2xl sm:text-3xl font-black text-rf-ink mb-6">
              Cook it tonight
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {recipes.map((r) => {
                const total = (r.prep_minutes || 0) + (r.cook_minutes || 0);
                return (
                  <Link
                    key={r.recipe_id}
                    to={`/r/${r.recipe_id}`}
                    data-testid={`kh-recipe-${r.recipe_id}`}
                    className="group bg-white rounded-2xl overflow-hidden border border-rf-ink/5 hover:border-rf-amber transition"
                  >
                    <div className="aspect-[4/3] bg-rf-cream-alt overflow-hidden">
                      {r.hero_image_url ? (
                        <img
                          src={r.hero_image_url}
                          alt={r.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-rf-ink/20 text-xs">No photo</div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-display text-lg font-black text-rf-ink group-hover:text-rf-orange leading-tight line-clamp-2">
                        {r.title || "Untitled"}
                      </h3>
                      <div className="mt-2 flex items-center gap-3 text-xs text-rf-ink-soft">
                        {total > 0 && <span>⏱ {total} min</span>}
                        {r.servings ? <span>· serves {r.servings}</span> : null}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Cross-sell / CTA */}
        <div className="mt-16 bg-rf-ink rounded-3xl p-8 text-rf-cream flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
          <div>
            <p className="overline text-rf-amber mb-2">Recipe Filer</p>
            <h2 className="font-display text-2xl font-black leading-tight">Save family recipes forever.</h2>
            <p className="text-rf-cream/70 mt-2 text-sm max-w-lg">Scan handwritten cards, import from any link, or type them in — organised into shareable cookbooks, printable at Lulu.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/kitchen-help" className="inline-flex items-center gap-2 bg-rf-cream/10 hover:bg-rf-amber hover:text-rf-ink px-5 py-2.5 rounded-full font-bold text-sm">
              <ArrowLeft size={16} weight="bold" /> All terms
            </Link>
            <Link to="/directory" className="inline-flex items-center gap-2 bg-rf-amber text-rf-ink px-5 py-2.5 rounded-full font-bold text-sm hover:bg-rf-cream">
              Browse recipes
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
